/* Office.js helpers — read the selection and write back as NATIVE tracked changes. */
import { findSpan, normWithMap } from "./match";

/** Returns the currently selected text in the Word document (empty string if none). */
export async function getSelectedText(): Promise<string> {
  return Word.run(async (ctx) => {
    const sel = ctx.document.getSelection();
    sel.load("text");
    await ctx.sync();
    return sel.text ?? "";
  });
}

/**
 * Replace the current selection with `newText`, recorded as a NATIVE Word tracked change.
 * We turn on change tracking first, so the author accepts/rejects with Word's Review tab.
 */
export async function replaceSelectionTracked(newText: string): Promise<void> {
  return Word.run(async (ctx) => {
    // TrackAll → every edit (including ours) is recorded as a revision.
    // Guarded: some Word builds/API sets may not expose changeTrackingMode; if so, we still
    // insert (the author can enable Track Changes manually).
    try {
      if ((Word as any).ChangeTrackingMode && "changeTrackingMode" in ctx.document) {
        ctx.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      }
    } catch {
      /* tracking not settable in this build — continue */
    }
    const sel = ctx.document.getSelection();
    sel.insertText(newText, Word.InsertLocation.replace);
    await ctx.sync();
  });
}

/** Whether there is a non-empty selection right now. */
export async function hasSelection(): Promise<boolean> {
  return (await getSelectedText()).trim().length > 0;
}

/** Folder of the currently open document (empty if unsaved / unknown). */
export function getDocumentFolder(): Promise<string> {
  return new Promise((resolve) => {
    try {
      Office.context.document.getFilePropertiesAsync({}, (res: Office.AsyncResult<Office.FileProperties>) => {
        const url = (res && res.value && res.value.url) || "";
        const i = Math.max(url.lastIndexOf("/"), url.lastIndexOf("\\"));
        resolve(i > 0 ? url.slice(0, i) : "");
      });
    } catch {
      resolve("");
    }
  });
}

/** Full text of the document body. */
export async function getDocumentText(): Promise<string> {
  return Word.run(async (ctx) => {
    const b = ctx.document.body;
    b.load("text");
    await ctx.sync();
    return b.text ?? "";
  });
}

/** Word's search chokes on `^` (its own escape char) and on strings over ~255 chars. */
function searchable(s: string): boolean {
  return s.length > 0 && s.length <= 255 && !s.includes("^");
}

/**
 * Find `excerpt` in the document and replace it with `fix` as a tracked change.
 *
 * The model quotes the manuscript almost — but not exactly — literally: it straightens curly
 * quotes, turns em dashes into hyphens, collapses whitespace. So we try, in order:
 *   1. Word's own search (fast path, exact quote).
 *   2. Per paragraph: normalized match that recovers the ORIGINAL substring, then searches that.
 *   3. Same paragraph rebuilt around the fix — last resort when search still refuses.
 * Returns false only when the excerpt truly isn't in the document.
 */
export async function applyFixTracked(excerpt: string, fix: string): Promise<boolean> {
  return Word.run(async (ctx) => {
    try {
      if ((Word as any).ChangeTrackingMode && "changeTrackingMode" in ctx.document) {
        ctx.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      }
    } catch {
      /* */
    }

    // 1) Camino rápido: búsqueda literal de Word.
    const q = excerpt.replace(/\s+/g, " ").trim().slice(0, 200);
    if (searchable(q)) {
      const results = ctx.document.body.search(q, { matchCase: false, ignoreSpace: true });
      results.load("items");
      await ctx.sync();
      if (results.items.length) {
        results.items[0].insertText(fix, Word.InsertLocation.replace);
        await ctx.sync();
        return true;
      }
    }

    // 2) Búsqueda tolerante, párrafo a párrafo.
    const ps = ctx.document.body.paragraphs;
    ps.load("items/text");
    await ctx.sync();

    for (const p of ps.items) {
      const span = findSpan(p.text, excerpt);
      if (!span) continue;

      // El tramo ORIGINAL (con sus comillas tipográficas y rayas) sí lo encuentra Word.
      const original = p.text.slice(span.start, span.end);
      if (searchable(original)) {
        const r = p.search(original, { matchCase: true });
        r.load("items");
        await ctx.sync();
        if (r.items.length) {
          r.items[0].insertText(fix, Word.InsertLocation.replace);
          await ctx.sync();
          return true;
        }
      }

      // 3) Último recurso: reescribimos el párrafo con el arreglo dentro.
      const rebuilt = (p.text.slice(0, span.start) + fix + p.text.slice(span.end))
        .replace(/\s*\n+\s*/g, " ")
        .trim();
      p.insertText(rebuilt, Word.InsertLocation.replace);
      await ctx.sync();
      return true;
    }

    return false;
  });
}

/** Text of every paragraph in the document body (index-aligned). */
export async function getParagraphTexts(): Promise<string[]> {
  return Word.run(async (ctx) => {
    const ps = ctx.document.body.paragraphs;
    ps.load("items/text");
    await ctx.sync();
    return ps.items.map((p) => p.text);
  });
}

/**
 * Replace the paragraph whose current text is `original`, as a NATIVE tracked change.
 *
 * We match by TEXT, never by index: with change tracking on, Word reflows the document between
 * calls, so a paragraph index captured up front goes stale and ends up pointing at something
 * that can't be edited (`GeneralException @ Paragraph.insertText`).
 *
 * Returns false when the paragraph is gone or the agent returned nothing — never throws for those.
 */
export async function replaceParagraphByText(original: string, newText: string): Promise<boolean> {
  // Keep it a single paragraph, and never call insertText("") — Word rejects it.
  const clean = newText.replace(/\s*\n+\s*/g, " ").trim();
  if (!clean) return false;

  return Word.run(async (ctx) => {
    try {
      if ((Word as any).ChangeTrackingMode && "changeTrackingMode" in ctx.document) {
        ctx.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      }
    } catch {
      /* */
    }
    const ps = ctx.document.body.paragraphs;
    ps.load("items/text");
    await ctx.sync();

    const target = normWithMap(original).norm;
    const p = ps.items.find((x) => normWithMap(x.text).norm === target);
    if (!p) return false;

    p.insertText(clean, Word.InsertLocation.replace);
    await ctx.sync();
    return true;
  });
}

/**
 * El texto de los cambios que siguen sin revisar.
 *
 * Sirve para no confundir «el autor lo aceptó» con «el autor todavía no ha mirado». Un cambio
 * pendiente no es una decisión, y contarlo como aceptada sería ponerle palabras en la boca.
 *
 * `getTrackedChanges` es WordApi 1.6: donde no exista se devuelve una lista vacía y la
 * reconciliación se queda solo con la presencia en el cuerpo, que es lo que había antes.
 */
export async function getPendingChangeTexts(): Promise<string[]> {
  try {
    return await Word.run(async (ctx) => {
      const body = ctx.document.body as any;
      if (typeof body.getTrackedChanges !== "function") return [];
      const cambios = body.getTrackedChanges();
      cambios.load("items/text,items/type");
      await ctx.sync();
      return (cambios.items || [])
        .filter((c: any) => c.type === "Added" || c.type === Word.ChangeTrackingState?.added)
        .map((c: any) => String(c.text || ""))
        .filter(Boolean);
    });
  } catch {
    return [];   // build sin soporte, o documento sin cambios: no es un error
  }
}

/**
 * «LO QUE QUEDA SIN DECIDIR SE DA POR ACEPTADO» (14-oct-2026). Rais: «los cambios que quedan pendientes en el Word se
 * considerarán aceptados, porque no podemos dejar cosas sueltas al final».
 *
 * Es lo mismo que «Revisar → Aceptar → Aceptar todos los cambios» de Word, en el documento abierto, y después guardar:
 * lo hace Word, no el programa, así que no aparece ningún Word nuevo y se deshace con Deshacer. Solo lo pide un clic
 * del autor (con su confirmación). `getTrackedChanges` y `acceptAll` son WordApi 1.6: donde no estén, se dice y no se
 * toca nada. Devuelve cuántos había, o por qué no se ha hecho.
 *
 * PROBAR EN WORD antes de publicar (regla del 9-oct: ninguna llamada nueva a Word sin probarla en uno de verdad).
 */
export async function aceptarLosPendientesYGuardar(): Promise<{ ok: true; aceptados: number } | { ok: false; motivo: "sin_api" | "fallo" | "sin_guardar"; aceptados?: number; error?: string }> {
  // Si Word los acepta y después no guarda, no es «no he podido»: están aceptados y falta guardar (equipo rojo, 14-oct).
  let aceptados: number | null = null;
  try {
    if (!Office.context.requirements.isSetSupported("WordApi", "1.6")) return { ok: false, motivo: "sin_api" };
    return await Word.run(async (ctx) => {
      const body = ctx.document.body as any;
      if (typeof body.getTrackedChanges !== "function") return { ok: false as const, motivo: "sin_api" as const };
      const cambios = body.getTrackedChanges();
      cambios.load("items");
      await ctx.sync();
      const n = (cambios.items || []).length;
      if (n > 0) { cambios.acceptAll(); await ctx.sync(); }
      aceptados = n;
      ctx.document.save();
      await ctx.sync();
      return { ok: true as const, aceptados: n };
    });
  } catch (e: any) {
    if (aceptados != null) return { ok: false, motivo: "sin_guardar", aceptados, error: String(e?.message ?? e).slice(0, 200) };
    return { ok: false, motivo: "fallo", error: String(e?.message ?? e).slice(0, 200) };
  }
}

/* ── El guion del escritor ───────────────────────────────────────────────────────────────────
 *
 * 9-oct-2026: la primera versión de la pestaña Guion buscaba SOLA la escena del cursor —un aviso en
 * cada movimiento del cursor, búsquedas con comodines por todo el documento, rangos ampliados— sin
 * haberse probado nunca en Word, y colgó el Word de Rais (abierto desde hacía doce días) a los tres
 * minutos de instalarla. Aquí queda solo lo que ya estaba probado con clientes: preguntar por el
 * documento y leer la selección cuando el autor lo pide. Nada de esta pestaña habla con Word sin un
 * clic, y cualquier llamada nueva a Word se prueba en Word antes de entrar (qa/guion-panel-guardias).
 */

/** La dirección del documento abierto tal como la da Word: una ruta, un file:// o un https:// («» si no está guardado). */
export function getDocumentUrl(): Promise<string> {
  return new Promise((resolve) => {
    try {
      Office.context.document.getFilePropertiesAsync({}, (res: Office.AsyncResult<Office.FileProperties>) => {
        resolve((res && res.value && res.value.url) || "");
      });
    } catch {
      resolve("");
    }
  });
}

/**
 * Cambia el ancho del panel de Word (Rais, 11-oct-2026: «la barra es demasiado estrecha»). Es una orden de Office
 * (TaskPaneApi 1.1), no del documento: no lee ni toca el texto. Devuelve si ESTE Word sabe hacerlo. Ojo: si el
 * ancho pedido no cabe (el panel no puede pasar de media ventana de Word), Word no hace nada y no avisa.
 */
export function ponerAnchoDelPanel(px: number): boolean {
  try {
    if (!Office.context.requirements.isSetSupported("TaskPaneApi", "1.1")) return false;
    Office.extensionLifeCycle.taskpane.setWidth(Math.round(px));
    return true;
  } catch {
    return false;
  }
}
